// Editable list of the colleges concierge staff are placing this athlete into,
// for the internal Concierge page. Staff-authored and staff-only — distinct
// from Visits (a visit that happened) and from the athlete's own Interested /
// Following buckets.
//
// Starts empty. "+ Add school" opens ONE picker at a time; choosing a school
// saves it immediately and closes the picker. School names resolve to a unitid
// against the same static /data/colleges.json the visits editor uses, so a
// target school and a visit to the same place agree on the crest and the id.
//
// Styled with the concierge ledger classes (ConciergePage.css .cn-v* / .cn-ts*).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import CollegeLogo from '../CollegeLogo';
import CollegePicker from '../CollegePicker';
import { visitFor } from '../../lib/conciergeSchoolsModel';
import { utcTodayIso } from '../../lib/conciergeTodoModel';
import { fmtMonDay } from '../../lib/conciergeActivityModel';

export default function TargetSchoolsEditor({ userId, onSaved, visits = [], onSchoolsChange }) {
  const { API_URL } = useAuth();
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/concierge/${userId}/target-schools`, cfg())
      .then(res => setSchools(Array.isArray(res.data) ? res.data : []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load target schools.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [userId, API_URL]);

  useEffect(() => { if (onSchoolsChange) onSchoolsChange(schools); }, [schools, onSchoolsChange]);

  const openPicker = () => {
    setError('');
    setQuery('');
    setPicking(true);   // the picker focuses itself on mount
  };

  const closePicker = () => {
    setPicking(false);
    setQuery('');
  };

  const add = (college) => {
    // Already on the list — say so rather than firing a request the server
    // will reject with a 409.
    if (college.u != null && schools.some(s => s.unitid === college.u)) {
      setError(`${college.n} is already a target school.`);
      closePicker();
      return;
    }
    setSaving(true);
    axios.post(
      `${API_URL}/concierge/${userId}/target-schools`,
      { unitid: college.u ?? null, college_name: college.n },
      cfg(),
    )
      .then(res => {
        setSchools(prev => [...prev, res.data]);
        closePicker();
        if (onSaved) onSaved();
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to add the school.'))
      .finally(() => setSaving(false));
  };

  const remove = (row) => {
    setError('');
    axios.delete(`${API_URL}/concierge/${userId}/target-schools/${row.id}`, cfg())
      .then(() => {
        setSchools(prev => prev.filter(s => s.id !== row.id));
        if (onSaved) onSaved();
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to remove the school.'));
  };

  const today = utcTodayIso();

  return (
    <section className="cn-card">
      <div className="cn-card__head">
        <h2 className="cn-card__title">Target schools</h2>
        <span className="cn-card__sub">Where staff are placing this athlete · internal</span>
        {!picking && (
          <button type="button" className="cn-dbtn" style={{ marginLeft: 'auto' }} onClick={openPicker}>+ Add school</button>
        )}
      </div>

      {error && <div className="cn-card__err"><span className="cn-inline-msg cn-inline-msg--err">{error}</span></div>}

      {picking && (
        <div className="cn-ts-picker">
          <div className="cn-ts-picker__field">
            <CollegePicker
              value={query}
              onInputChange={setQuery}
              onSelect={add}
              autoFocus
              disabled={saving}
              placeholder="Search college…"
              ariaLabel="Search for a college to add as a target school"
              className="cn-vinput"
              inputProps={{ onKeyDown: (e) => { if (e.key === 'Escape') closePicker(); } }}
              rootStyle={{ flex: 1, minWidth: 0 }}
              classNames={{
                popup: 'cn-autocomplete',
                option: 'cn-autocomplete__item',
                optionActive: 'cn-autocomplete__item--active',
                footer: 'cn-autocomplete__hint',
              }}
              renderItem={(c) => (
                <>
                  <div className="cn-autocomplete__name">{c.n}</div>
                  <div className="cn-autocomplete__loc">{[c.c, c.s].filter(Boolean).join(', ')}</div>
                </>
              )}
            />
          </div>
          <button type="button" className="cn-vdelete" onClick={closePicker}>Cancel</button>
        </div>
      )}

      {loading ? (
        <div className="cn-loading">Loading…</div>
      ) : (
        <div>
          {schools.length === 0 && !picking && (
            <div className="cn-empty-line">No target schools yet.</div>
          )}
          {schools.length > 0 && (
            <div className="cn-tsrow cn-tsrow--head">
              <div>School</div><div>Division</div><div>Visit</div><div>Added</div><div />
            </div>
          )}
          {schools.map(s => {
            const v = visitFor(s, visits, today);
            return (
              <div className="cn-tsrow" key={s.id}>
                <div className="cn-tsrow__school">
                  <CollegeLogo schoolName={s.college_name} logoUrl={s.logo_url} unitid={s.unitid} size={32} />
                  <div>
                    <div className="cn-tsrow__name">{s.college_name}</div>
                    {s.state && <div className="cn-tsrow__loc">{s.state}</div>}
                  </div>
                </div>
                <div>{s.division || '—'}</div>
                <div className={`cn-visitcol cn-visitcol--${v.tone}`}>{v.text}</div>
                <div className="cn-tsrow__added">{s.added_by_name}{s.created_at ? ` · ${fmtMonDay(s.created_at)}` : ''}</div>
                <button type="button" className="cn-remove" aria-label={`Remove ${s.college_name}`} onClick={() => remove(s)}>Remove</button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
